import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as authApi from "../api/auth";
import * as accesosApi from "../api/accesos";
import * as monedasApi from "../api/monedas";

const MONEDA_POR_DEFECTO = { codigo: "USD", simbolo: "$" };

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(() => {
    const stored = localStorage.getItem("fs_user");
    return stored ? JSON.parse(stored) : null;
  });
  const [menu, setMenu] = useState([]);
  const [cargandoMenu, setCargandoMenu] = useState(false);
  const [monedaPredeterminada, setMonedaPredeterminada] = useState(MONEDA_POR_DEFECTO);

  const cargarMenu = useCallback(async () => {
    setCargandoMenu(true);
    try {
      const permisos = await accesosApi.misPermisos();
      setMenu(permisos);
    } catch {
      setMenu([]);
    } finally {
      setCargandoMenu(false);
    }
  }, []);

  const cargarMonedaPredeterminada = useCallback(async () => {
    try {
      const monedas = await monedasApi.listarMonedas();
      const predeterminada = monedas.find((m) => m.predeterminada);
      setMonedaPredeterminada(predeterminada
        ? { codigo: predeterminada.codigo, simbolo: predeterminada.simbolo }
        : MONEDA_POR_DEFECTO);
    } catch {
      setMonedaPredeterminada(MONEDA_POR_DEFECTO);
    }
  }, []);

  useEffect(() => {
    if (usuario) {
      cargarMenu();
      cargarMonedaPredeterminada();
    }
  }, [usuario, cargarMenu, cargarMonedaPredeterminada]);

  /** Guarda un token/perfil actualizado (de login o de /perfil/refrescar-token) tanto en
   * localStorage como en el estado en memoria -- para que un cambio hecho en la misma sesión
   * (ej. crear/unirse/salir de una familia) se refleje sin tener que cerrar sesión y volver a entrar. */
  const actualizarSesion = (data) => {
    const usuarioData = {
      nombres: data.nombres,
      email: data.email,
      perfil: data.perfil,
      emailValidado: data.emailValidado,
      accesoPagosExtra: data.accesoPagosExtra,
      familiaDecisionTomada: data.familiaDecisionTomada,
      nombreFamilia: data.nombreFamilia,
    };
    localStorage.setItem("fs_token", data.token);
    localStorage.setItem("fs_user", JSON.stringify(usuarioData));
    setUsuario(usuarioData);
    return usuarioData;
  };

  const iniciarSesion = async (email, password) => {
    const data = await authApi.login(email, password);
    return actualizarSesion(data);
  };

  const cerrarSesion = () => {
    localStorage.removeItem("fs_token");
    localStorage.removeItem("fs_user");
    setUsuario(null);
    setMenu([]);
    setMonedaPredeterminada(MONEDA_POR_DEFECTO);
  };

  const esAdministrador = usuario?.perfil === "Administrador";

  return (
    <AuthContext.Provider
      value={{ usuario, menu, cargandoMenu, esAdministrador, monedaPredeterminada, iniciarSesion, cerrarSesion, actualizarSesion }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
